import { useEffect } from "react";
import { X } from "lucide-react";
import { NativeLikeSelect } from "./CocoaSelect";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="cocoa-page-header mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-[#3E2723]">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[#6D4C41]">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <div data-cocoa-card="true" className={`cocoa-card bg-white rounded-2xl border border-[#EFEBE0] shadow-[0_4px_24px_-6px_rgba(78,52,46,0.08)] transition-shadow duration-200 hover:shadow-[0_8px_32px_-6px_rgba(78,52,46,0.12)] ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const styles = {
    primary: "bg-[#4E342E] text-white hover:bg-[#3E2723] shadow-[0_4px_12px_rgba(78,52,46,0.15)]",
    outline: "border border-[#D7CCC8] text-[#4E342E] hover:bg-[#F5F2EB]",
    ghost: "text-[#6D4C41] hover:bg-[#F5F2EB]",
    danger: "bg-[#E53935] text-white hover:bg-[#C62828]",
  };
  return (
    <button className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#8D6E63] focus:ring-offset-2 disabled:opacity-50 active:scale-[0.98] ${styles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Input(props) {
  return <input {...props} className={`flex h-10 w-full rounded-xl border border-[#EFEBE0] bg-white px-3.5 py-2 text-sm placeholder:text-[#84675B] focus:outline-none focus:ring-2 focus:ring-[#8D6E63] focus:border-transparent ${props.className || ""}`} />;
}

export function Select({ children, className = "", ...props }) {
  return (
    <NativeLikeSelect {...props}
      triggerClass={`flex h-10 w-full rounded-xl border-[#EFEBE0] bg-white px-3 py-2 text-sm text-[#3E2723] focus:ring-2 focus:ring-[#8D6E63] ${className}`}>
      {children}
    </NativeLikeSelect>
  );
}

export function Label({ children }) {
  return <label className="mb-1 block text-xs font-bold uppercase tracking-[0.05em] text-[#7B6157]">{children}</label>;
}

const badgeStyles = {
  active: "bg-[#E8F5E9] text-[#2E7D32]",
  scheduled: "bg-[#E3F2FD] text-[#1565C0]",
  open: "bg-[#FFF8E1] text-[#876600]",
  pending: "bg-[#FFF8E1] text-[#876600]",
  high: "bg-[#FFEBEE] text-[#C62828]",
  urgent: "bg-[#B71C1C] text-white",
  overdue: "bg-[#FFEBEE] text-[#C62828]",
  expiring: "bg-[#FFF3E0] text-[#B84A00]",
  medium: "bg-[#FFF3E0] text-[#B84A00]",
  low: "bg-[#F5F2EB] text-[#6D4C41]",
  discharged: "bg-[#F5F2EB] text-[#6D4C41]",
  archived: "bg-[#ECEFF1] text-[#546E7A]",
  inactive: "bg-[#F5F2EB] text-[#6D4C41]",
  referred: "bg-[#EDE7F6] text-[#5E35B1]",
  completed: "bg-[#E8F5E9] text-[#2E7D32]",
  approved: "bg-[#E8F5E9] text-[#2E7D32]",
  denied: "bg-[#FFEBEE] text-[#C62828]",
  due: "bg-[#FFF8E1] text-[#876600]",
  new: "bg-[#E3F2FD] text-[#1565C0]",
  signed: "bg-[#E8F5E9] text-[#2E7D32]",
  pending_cosign: "bg-[#FFF3E0] text-[#B84A00]",
  draft: "bg-[#F5F2EB] text-[#6D4C41]",
  in_progress: "bg-[#E3F2FD] text-[#1565C0]",
  manager_review: "bg-[#EDE7F6] text-[#5E35B1]",
  submitted: "bg-[#E8F5E9] text-[#2E7D32]",
  request_queue: "bg-[#FFF3E0] text-[#B84A00]",
  critical: "bg-[#B71C1C] text-white",
  tbd: "bg-[#ECEFF1] text-[#546E7A]",
  expiring: "bg-[#FFF8E1] text-[#8D5A00]",
  expired: "bg-[#FFEBEE] text-[#C62828]",
  unsigned: "bg-[#FFF3E0] text-[#B84A00]",
  exhausted: "bg-[#FFEBEE] text-[#C62828]",
  expired: "bg-[#FFEBEE] text-[#C62828]",
};

export function Badge({ value }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${badgeStyles[value] || "bg-[#F5F2EB] text-[#6D4C41]"}`}>
      {String(value).replace("_", " ")}
    </span>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#3E2723]/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-2xl bg-white p-6 shadow-2xl max-h-[85vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">{title}</h3>
          <button type="button" data-testid="modal-close-btn" aria-label="Close" onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#F5F2EB] transition-colors">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto rounded-2xl">
      <table className="w-full text-sm text-left border-collapse">
        <thead>
          <tr>{headers.map((h) => <th key={h} className="bg-[#F5F2EB] text-[#6D4C41] font-semibold py-3 px-4 text-xs uppercase tracking-wide first:rounded-tl-2xl last:rounded-tr-2xl">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, className = "", ...props }) {
  return <td {...props} className={`border-b border-[#EFEBE0] py-3 px-4 tabular-nums text-[#4E342E] ${className}`}>{children}</td>;
}

export function Empty({ text }) {
  return <div className="py-10 text-center text-sm text-[#84675B]">{text}</div>;
}
